import { memo, useCallback, useState } from 'react';

import {
	Box,
	Button,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Table,
	Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import PassTypesForm from '@/components/pass-types-form/pass-types-form';
import PassTypesItem from '@/components/pass-types-item/pass-types-item';

import {
	useCreatePassTypeMutation,
	useDeletePassTypeMutation,
	useGetPassTypesQuery,
	useUpdatePassTypeMutation,
} from '@/api/pass-types';

import { PassTypeRequestBodyType } from '@/types/pass-types';
import { PassType } from '@/types/person';

import { chunk } from '@/shared/helpers/utils';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

function PassTypes() {
	const { data: passTypes, isLoading: isPassesLoading } = useGetPassTypesQuery('passtypes');

	const [updateType, { isLoading: isUpdateLoading }] = useUpdatePassTypeMutation();
	const [createType, { isLoading: isCreateLoading }] = useCreatePassTypeMutation();
	const [deleteType] = useDeletePassTypeMutation();

	const [passType, setPassType] = useState<PassType | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activePage, setPage] = useState(1);

	const handleEdit = (item: PassType) => {
		setPassType(item);
		setIsModalOpen(true);
	};

	const handleCreate = async (body: PassTypeRequestBodyType) => {
		try {
			await createType(body);
			setIsModalOpen(false);
		} catch (e) {
			void 0;
		}
	};

	const handleUpdate = async (body: PassTypeRequestBodyType, id: number) => {
		try {
			await updateType({ id, body });
			setIsModalOpen(false);
		} catch (e) {
			void 0;
		}
	};

	const handleClose = () => {
		setPassType(null);
		setIsModalOpen(false);
	};

	const handleDelete = useCallback(
		async (id: number) => {
			await deleteType(id);
		},
		[deleteType],
	);

	const chunked = chunk(passTypes?.objects?.filter((passType) => !passType?.is_deleted) ?? [], 10);

	const rows = chunked[activePage - 1]?.map((elem) => (
		<PassTypesItem
			key={elem.id}
			passType={elem}
			handleEdit={handleEdit}
			handleDelete={handleDelete}
		/>
	));

	return (
		<>
			<LayoutRow
				title={{
					title: { children: 'Типы пропусков', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				<Box
					pos="relative"
					style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
				>
					<LoadingOverlay
						visible={isPassesLoading}
						zIndex={1000}
						overlayProps={{ radius: 2, blur: 2 }}
					/>
					<Group>
						<Button
							variant="filled"
							radius="lg"
							onClick={() => {
								setIsModalOpen(true);
							}}
						>
							<Text fw={500}>Добавить тип пропуска</Text>
						</Button>
					</Group>
					<ScrollArea h={'100vh'} pr={'md'} scrollbarSize={12} offsetScrollbars>
						<Table verticalSpacing="md" stickyHeader>
							<Table.Thead>
								<Table.Tr>
									<Table.Th>
										<Text size="md" c="dimmed">
											Название
										</Text>
									</Table.Th>
									<Table.Th>
										<Text size="md" c="dimmed">
											Тип
										</Text>
									</Table.Th>
									<Table.Th>
										<Text size="md" c="dimmed">
											ID
										</Text>
									</Table.Th>
									<Table.Th ta="center">
										<Text size="md" c="dimmed">
											Действия
										</Text>
									</Table.Th>
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>{rows}</Table.Tbody>
						</Table>
					</ScrollArea>
					<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
				</Box>
			</LayoutRow>
			<PassTypesForm
				isOpen={isModalOpen}
				setIsOpen={setIsModalOpen}
				passType={passType}
				onUpdate={handleUpdate}
				onCreate={handleCreate}
				handleClose={handleClose}
			/>
		</>
	);
}

export default memo(PassTypes);
